import { useExamContext } from "@features/exam/contexts/ExamContext";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import type { FeedbackParams } from "@memorang/features/src/feedback/types";
import Constants from "expo-constants";
import { usePathname } from "expo-router";
import { useMemo } from "react";
import { Platform } from "react-native";
import useClerkUser from "./useClerkUser";

type UseFeedbackParamsOptions = {
	view?: string;
	type?: "Review" | "Content Flagged";
	itemId?: string;
	sessionId?: string;
	threadId?: string;
	runId?: string;
	featureName?: string;
	sessionType?: string;
	rating?: string;
	itemVersionId?: string;
};

type ExtendedFeedbackParams = FeedbackParams & {
	aiThreadId?: string;
	aiRunId?: string;
	featureName?: string;
};

export const useFeedbackParams = (
	options: UseFeedbackParamsOptions = {},
): ExtendedFeedbackParams => {
	const pathname = usePathname();
	const companyName = useAppStore((store) => store.companyName);
	const appName = useAppStore((store) => store.app.name);
	const {
		currentExamContext: { examName },
	} = useExamContext();

	const { email, username } = useClerkUser();

	const {
		view,
		type,
		itemId,
		sessionId,
		threadId,
		runId,
		rating,
		featureName,
		sessionType,
	} = options;

	return useMemo(
		() => ({
			email: email ?? "",
			userName: username ?? "",
			appName,
			platform: Platform.OS,
			appLanguage: i18n.language,
			currentAppVersion: Constants.expoConfig?.version,
			view: view ?? pathname,
			type: (type as "Review" | "Content Flagged") ?? ("Review" as const),
			companyName,
			bundleName: examName,
			projectId: 0,
			organizationId: 0,
			...(itemId && {
				itemId,
				itemVersionId: "1",
				sessionType: sessionType ?? "FORMATIVE",
			}),
			...(sessionId && { sessionId }),
			...(threadId && { threadId }),
			...(runId && { runId }),
			...(rating && {
				rating: rating as "positive" | "negative",
			}),
			...(featureName && { featureName }),
		}),
		[
			appName,
			pathname,
			companyName,
			examName,
			view,
			type,
			email,
			username,
			itemId,
			sessionId,
			threadId,
			runId,
			rating,
			featureName,
			sessionType,
		],
	);
};
